import { createSlice } from '@reduxjs/toolkit';

const INITIAL_STATE = {
  // Navigation
  isSideMenuOpen: false,
  activeMenuItem: null,
  isInHeroSection: true, // Control para el fondo del NavBar basado en la sección Hero

  // Theme and Layout
  theme: 'light', // 'light' | 'dark' | 'lawyer' | 'client'
  userTypeTheme: null, // 'lawyer' | 'client' - para cambios dinámicos de tema

  // Modals and Overlays
  modals: {
    isConfirmDialogOpen: false,
    confirmDialogData: null,
    isNotificationModalOpen: false,
    notificationData: null,
  },

  // Notifications and Alerts
  notifications: [], // Array de notificaciones
  alerts: [], // Array de alertas temporales

  // Loading states
  globalLoading: false,
  loadingStates: {}, // Para loading específicos por componente/acción

  // Form UI states
  formStates: {
    currentStep: 0,
    totalSteps: 1,
    isFormDirty: false,
    showValidationErrors: false,
  },

  // Dashboard states (para futuro)
  dashboard: {
    sidebarCollapsed: false,
    activeTab: 'overview',
    layout: 'grid', // 'grid' | 'list'
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState: INITIAL_STATE,
  reducers: {
    // Side Menu Actions
    openSideMenu: state => {
      state.isSideMenuOpen = true;
    },

    closeSideMenu: state => {
      state.isSideMenuOpen = false;
    },

    toggleSideMenu: state => {
      state.isSideMenuOpen = !state.isSideMenuOpen;
    },

    setActiveMenuItem: (state, action) => {
      state.activeMenuItem = action.payload;
    },

    // Navigation Actions
    navigateToHome: state => {
      state.activeMenuItem = 'home';
      state.isInHeroSection = true;
    },

    navigateToDashboard: state => {
      state.activeMenuItem = 'dashboard';
      state.isInHeroSection = false;
    },

    navigateToClientDashboard: state => {
      state.activeMenuItem = 'client-dashboard';
      state.isInHeroSection = false;
      state.userTypeTheme = 'client';
    },

    navigateToLawyerDashboard: state => {
      state.activeMenuItem = 'lawyer-dashboard';
      state.isInHeroSection = false;
      state.userTypeTheme = 'lawyer';
    },

    navigateToEspecialidades: state => {
      state.activeMenuItem = 'especialidades';
      state.isInHeroSection = false;
    },

    navigateToQuienesSomos: state => {
      state.activeMenuItem = 'quienes-somos';
      state.isInHeroSection = false;
    },

    logout: state => {
      state.activeMenuItem = 'home';
      state.isInHeroSection = true;
      state.userTypeTheme = null;
      state.theme = 'light';
    },

    // Hero Section Actions
    setIsInHeroSection: (state, action) => {
      state.isInHeroSection = action.payload;
    },

    // Theme Actions
    setTheme: (state, action) => {
      state.theme = action.payload;
    },

    setUserTypeTheme: (state, action) => {
      state.userTypeTheme = action.payload;
      // Cambiar tema automáticamente basado en tipo de usuario
      state.theme = action.payload === 'lawyer' ? 'lawyer' : 'client';
    },

    toggleTheme: state => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
    },

    // Modal Actions
    openConfirmDialog: (state, action) => {
      state.modals.isConfirmDialogOpen = true;
      state.modals.confirmDialogData = action.payload;
    },

    closeConfirmDialog: state => {
      state.modals.isConfirmDialogOpen = false;
      state.modals.confirmDialogData = null;
    },

    openNotificationModal: (state, action) => {
      state.modals.isNotificationModalOpen = true;
      state.modals.notificationData = action.payload;
    },

    closeNotificationModal: state => {
      state.modals.isNotificationModalOpen = false;
      state.modals.notificationData = null;
    },

    // Notifications Actions
    addNotification: (state, action) => {
      const notification = {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        ...action.payload,
      };
      state.notifications.unshift(notification);

      // Limitar a máximo 50 notificaciones
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50);
      }
    },

    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      );
    },

    markNotificationAsRead: (state, action) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification) {
        notification.read = true;
      }
    },

    clearAllNotifications: state => {
      state.notifications = [];
    },

    // Alerts Actions (para mensajes temporales)
    addAlert: (state, action) => {
      const alert = {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        autoRemove: action.payload.autoRemove !== false, // por defecto true
        duration: action.payload.duration || 5000, // 5 segundos por defecto
        ...action.payload,
      };
      state.alerts.push(alert);
    },

    removeAlert: (state, action) => {
      state.alerts = state.alerts.filter(alert => alert.id !== action.payload);
    },

    clearAllAlerts: state => {
      state.alerts = [];
    },

    // Loading Actions
    setGlobalLoading: (state, action) => {
      state.globalLoading = action.payload;
    },

    setLoadingState: (state, action) => {
      const { key, loading } = action.payload;
      state.loadingStates[key] = loading;
    },

    clearLoadingState: (state, action) => {
      delete state.loadingStates[action.payload];
    },

    clearAllLoadingStates: state => {
      state.loadingStates = {};
    },

    // Form UI Actions
    setFormStep: (state, action) => {
      state.formStates.currentStep = action.payload;
    },

    setTotalFormSteps: (state, action) => {
      state.formStates.totalSteps = action.payload;
    },

    setFormDirty: (state, action) => {
      state.formStates.isFormDirty = action.payload;
    },

    setShowValidationErrors: (state, action) => {
      state.formStates.showValidationErrors = action.payload;
    },

    resetFormStates: state => {
      state.formStates = INITIAL_STATE.formStates;
    },

    // Dashboard Actions (para futuro)
    toggleDashboardSidebar: state => {
      state.dashboard.sidebarCollapsed = !state.dashboard.sidebarCollapsed;
    },

    setDashboardActiveTab: (state, action) => {
      state.dashboard.activeTab = action.payload;
    },

    setDashboardLayout: (state, action) => {
      state.dashboard.layout = action.payload;
    },

    // Reset Actions
    resetUI: state => {
      return INITIAL_STATE;
    },
  },
});

// Export actions
export const {
  // Side Menu
  openSideMenu,
  closeSideMenu,
  toggleSideMenu,
  setActiveMenuItem,

  // Navigation
  navigateToHome,
  navigateToDashboard,
  navigateToClientDashboard,
  navigateToLawyerDashboard,
  navigateToEspecialidades,
  navigateToQuienesSomos,
  logout,

  // Hero Section
  setIsInHeroSection,

  // Theme
  setTheme,
  setUserTypeTheme,
  toggleTheme,

  // Modals
  openConfirmDialog,
  closeConfirmDialog,
  openNotificationModal,
  closeNotificationModal,

  // Notifications
  addNotification,
  removeNotification,
  markNotificationAsRead,
  clearAllNotifications,

  // Alerts
  addAlert,
  removeAlert,
  clearAllAlerts,

  // Loading
  setGlobalLoading,
  setLoadingState,
  clearLoadingState,
  clearAllLoadingStates,

  // Form UI
  setFormStep,
  setTotalFormSteps,
  setFormDirty,
  setShowValidationErrors,
  resetFormStates,

  // Dashboard
  toggleDashboardSidebar,
  setDashboardActiveTab,
  setDashboardLayout,

  // Reset
  resetUI,
} = uiSlice.actions;

// Selectors
export const selectUI = state => state.ui;
export const selectIsSideMenuOpen = state => state.ui.isSideMenuOpen;
export const selectActiveMenuItem = state => state.ui.activeMenuItem;
export const selectIsInHeroSection = state => state.ui.isInHeroSection;
export const selectTheme = state => state.ui.theme;
export const selectUserTypeTheme = state => state.ui.userTypeTheme;
export const selectModals = state => state.ui.modals;
export const selectNotifications = state => state.ui.notifications;
export const selectUnreadNotifications = state => state.ui.notifications.filter(n => !n.read);
export const selectAlerts = state => state.ui.alerts;
export const selectGlobalLoading = state => state.ui.globalLoading;
export const selectLoadingState = key => state => state.ui.loadingStates[key] || false;
export const selectFormStates = state => state.ui.formStates;
export const selectDashboard = state => state.ui.dashboard;

// Computed selectors
export const selectIsLawyerTheme = state =>
  state.ui.theme === 'lawyer' || state.ui.userTypeTheme === 'lawyer';
export const selectIsClientTheme = state =>
  state.ui.theme === 'client' || state.ui.userTypeTheme === 'client';

export default uiSlice.reducer;
